import { NgClass } from '@angular/common';
import { Component, HostListener, inject, DOCUMENT } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';

/**
 * A standalone component that displays a "scroll to top" button when the user scrolls down the page.
 * The button appears when the user scrolls more than 200 pixels from the top and disappears when
 * scrolling back up.
 *
 * @example
 * ```html
 * <pax-scroll-to-top></pax-scroll-to-top>
 * ```
 */
@Component({
    selector: 'pax-scroll-to-top',
    standalone: true,
    imports: [NgClass, LucideAngularModule],
    template: `
        <button
            (click)="scrollToTop()"
            class="fixed right-8 bottom-8 z-50 p-4 bg-neutral-white rounded-full shadow-lg transition-all duration-300"
            [ngClass]="{
                'opacity-0 pointer-events-none': !showButton,
                'opacity-100': showButton,
            }"
            aria-label="Nach oben scrollen"
        >
            <lucide-icon name="chevron-up" [size]="24"></lucide-icon>
        </button>
    `,
})
export class ScrollToTopComponent {
    private readonly document = inject(DOCUMENT);

    /** Controls the visibility of the scroll-to-top button */
    showButton = false;

    /**
     * Listens to window scroll events and shows/hides the button based on scroll position.
     * The button becomes visible when the user scrolls more than 200 pixels down.
     */
    @HostListener('window:scroll')
    onWindowScroll() {
        this.showButton = (this.document.defaultView?.scrollY ?? 0) > 200;
    }

    /**
     * Smoothly scrolls the window to the top of the page when the button is clicked.
     */
    scrollToTop() {
        this.document.defaultView?.scrollTo({ top: 0, behavior: 'smooth' });
    }
}
